import React, { useEffect, useState } from "react";
import {
  deleteFeedModerationReport,
  dismissFeedModerationReport,
  getFeedModerationReports,
} from "../../HTTP/articles";
import Pagination from "../../Helper/Pagination";

const ITEMS_PER_PAGE = 5;

const formatDate = (value) => {
  if (!value) return "Date inconnue";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("fr-FR");
};

const getFullName = (person) =>
  `${person?.firstname || ""} ${person?.lastname || ""}`.trim() || "Utilisateur";

export default function FeedModerationAdmin() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const hasPermission = Boolean(user?.admin || user?.super_admin);
  const [reports, setReports] = useState({ count: 0, rows: [] });
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(window.location.search).get("query") || ""
  );
  const [appliedSearch, setAppliedSearch] = useState(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!hasPermission) return undefined;
    let active = true;
    const loadReports = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getFeedModerationReports(
          (currentPage - 1) * ITEMS_PER_PAGE,
          currentPage * ITEMS_PER_PAGE,
          appliedSearch
        );
        if (!active) return;
        setReports({
          count: Number(result?.count) || 0,
          rows: Array.isArray(result?.rows) ? result.rows : [],
        });
      } catch (requestError) {
        if (!active) return;
        setReports({ count: 0, rows: [] });
        setError(requestError?.message || "Les signalements n'ont pas pu être chargés.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadReports();
    return () => {
      active = false;
    };
  }, [appliedSearch, currentPage, hasPermission]);

  if (!hasPermission) {
    window.location.href = "/";
    return null;
  }

  const handleSearch = (event) => {
    event.preventDefault();
    const search = searchTerm.trim();
    setCurrentPage(1);
    setAppliedSearch(search);
    const params = new URLSearchParams(window.location.search);
    if (search) params.set("query", search);
    else params.delete("query");
    const query = params.toString();
    window.history.pushState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  };

  const removeHandledReport = (feedId) => {
    setReports((current) => ({
      count: Math.max(0, current.count - 1),
      rows: current.rows.filter((row) => Number(row.id) !== Number(feedId)),
    }));
  };

  const handleDismiss = async (feedId) => {
    if (!window.confirm("Écarter les signalements et laisser cette publication en ligne ?")) return;
    setProcessingId(feedId);
    setError("");
    try {
      await dismissFeedModerationReport(feedId);
      removeHandledReport(feedId);
    } catch (requestError) {
      setError(requestError?.message || "Les signalements n'ont pas pu être écartés.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (feedId) => {
    if (!window.confirm("Supprimer cette publication et son image ?")) return;
    const comment = window.prompt("Raison de la suppression (facultatif) :", "");
    if (comment === null) return;
    setProcessingId(feedId);
    setError("");
    try {
      await deleteFeedModerationReport(feedId, comment.trim());
      removeHandledReport(feedId);
    } catch (requestError) {
      setError(requestError?.message || "La publication n'a pas pu être supprimée.");
    } finally {
      setProcessingId(null);
    }
  };

  const paginate = (page) => setCurrentPage(page);

  return (
    <main className="w-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 mt-4">
        <h1 className="text-center text-2xl font-bold">Modération du fil d’actualité</h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          Publications faisant l’objet d’un ou plusieurs signalements en attente.
        </p>
      </div>

      <form onSubmit={handleSearch} className="mb-5 flex flex-col gap-2 sm:flex-row">
        <label htmlFor="feed-moderation-search" className="sr-only">
          Rechercher dans les signalements
        </label>
        <input
          id="feed-moderation-search"
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Texte, nom de l'auteur ou motif"
          className="min-w-0 flex-1 rounded border border-gray-300 px-3 py-2"
        />
        <button
          type="submit"
          className="rounded bg-zemusbis px-5 py-2 font-semibold text-white hover:bg-zemushover"
        >
          Rechercher
        </button>
      </form>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="rounded bg-white p-5 text-center shadow">Chargement des signalements...</p>
      ) : reports.rows.length === 0 ? (
        <p className="rounded bg-white p-5 text-center shadow">
          Aucun signalement en attente.
        </p>
      ) : (
        <div className="space-y-4">
          {reports.rows.map((post) => (
            <article key={post.id} className="rounded-lg bg-white p-4 shadow-md">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                {post.profile_picture && (
                  <img
                    src={post.profile_picture}
                    alt=""
                    className="h-12 w-12 rounded-full object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{getFullName(post)}</h2>
                  <p className="text-sm text-gray-500">
                    Publication du {formatDate(post.created_at)} · {post.reports_count || 0} signalement(s)
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={processingId === post.id}
                    onClick={() => handleDismiss(post.id)}
                    className="rounded border border-gray-400 px-3 py-2 text-sm font-semibold hover:bg-gray-100 disabled:opacity-60"
                  >
                    Écarter
                  </button>
                  <button
                    type="button"
                    disabled={processingId === post.id}
                    onClick={() => handleDelete(post.id)}
                    className="rounded bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {processingId === post.id ? "Traitement..." : "Supprimer"}
                  </button>
                </div>
              </div>

              {post.message && (
                <p className="mt-3 whitespace-pre-wrap break-words rounded bg-gray-50 p-3">
                  {post.message}
                </p>
              )}
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt="Illustration de la publication signalée"
                  className="mt-3 max-h-96 max-w-full rounded object-contain"
                />
              )}
              {(post.article_id || post.review_id) && (
                <p className="mt-3 text-sm text-gray-600">
                  Publication liée : {post.article_id ? `article #${post.article_id}` : `revue de presse #${post.review_id}`}
                </p>
              )}

              <section className="mt-4 border-t pt-3">
                <h3 className="mb-2 font-semibold">Motifs signalés</h3>
                <ul className="space-y-2">
                  {(Array.isArray(post.reports) ? post.reports : []).map((report) => (
                    <li key={report.id} className="rounded bg-gray-50 p-3 text-sm">
                      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
                        <span className="font-semibold">
                          {getFullName(report)} · {report.reason}
                        </span>
                        <time className="text-gray-500">{formatDate(report.created_at)}</time>
                      </div>
                      {report.comment && (
                        <p className="mt-1 whitespace-pre-wrap break-words">{report.comment}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            </article>
          ))}
        </div>
      )}

      {!loading && reports.count > ITEMS_PER_PAGE && (
        <Pagination
          itemsPerPage={ITEMS_PER_PAGE}
          totalItems={reports.count}
          paginate={paginate}
          currentPage={currentPage}
        />
      )}
    </main>
  );
}
